import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from './firebase';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  serverTimestamp,
  GeoPoint,
  query,
  where
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useJsApiLoader } from '@react-google-maps/api';
import AddressSearch from './AddressSearch';
import {
  getApplicableTariff,
  calculateFare,
  LARGE_PARTY_SURCHARGE,
  LARGE_PARTY_THRESHOLD,
} from './fareCalculator';
import './AdminPanel.css';

const ADMIN_EMAIL = 'ride@u-gotaxi.co.uk';
const TERMINAL_RIDE_STATUSES = ['completed', 'cancelled', 'cancelled_by_driver', 'cancelled_by_passenger', 'declined'];
const TRANSFERABLE_RIDE_STATUSES = ['pending', 'assigned', 'accepted', 'scheduled', 'awaiting_driver_response'];

const AdminPanel = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [activeTab, setActiveTab] = useState('promos'); // 'promos', 'booking', or 'driverBookings'

  // Load Google Maps API
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyBKQaifAZ7DdD62JkJXq8lBsHxHPr-mGxw',
    libraries: ['places', 'geometry'],
  });
  
  // Form data state
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discountType: 'percentage',
    discountValue: '',
    maxDiscount: '',
    minFare: '',
    minFinalFare: '',
    usageLimit: '',
    expiryDate: ''
  });

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Manual booking form state
  const [bookingData, setBookingData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    pickup: '',
    dropoff: '',
    numberOfRiders: 1,
    wheelchairRequired: false,
    fare: '',
    scheduledTime: '',
    notes: '',
    assignedDriverId: ''
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  
  // Address lookup and fare calculation state
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [driverBookings, setDriverBookings] = useState([]);
  const [driverBookingsLoading, setDriverBookingsLoading] = useState(false);
  const [transferSelection, setTransferSelection] = useState({});
  const [transferLoadingRideId, setTransferLoadingRideId] = useState('');

  const isAdminUser = user?.email === ADMIN_EMAIL;

  const formatFirebaseDateTime = useCallback((value) => {
    if (!value) return 'N/A';

    try {
      if (typeof value?.toDate === 'function') {
        return value.toDate().toLocaleString();
      }

      if (value instanceof Date) {
        return value.toLocaleString();
      }

      return new Date(value).toLocaleString();
    } catch (error) {
      return 'N/A';
    }
  }, []);

  const getRideDisplayStatus = useCallback((status) => {
    if (!status) return 'Unknown';
    return status.toString().replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }, []);

  const showToast = useCallback((message, type = 'default') => {
    try {
      toast(message, { type });
    } catch (error) {
      console.error('Toast error:', error);
    }
  }, []);

  const requiredVehicleCapacity = Math.max(Number(bookingData.numberOfRiders) || 1, 1);

  const getDriverVehicleCapacity = (driver) => {
    const capacity = Number(driver?.vehicleCapacity);
    return Number.isFinite(capacity) && capacity > 0 ? capacity : 4;
  };

  const driverMatchesBookingRequirements = useCallback((driver) => {
    if (getDriverVehicleCapacity(driver) < requiredVehicleCapacity) {
      return false;
    }

    if (bookingData.wheelchairRequired && !driver?.wheelchairAccessible) {
      return false;
    }

    return true;
  }, [bookingData.wheelchairRequired, requiredVehicleCapacity]);

  // Load promo codes
  const loadPromoCodes = useCallback(async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'promoCodes'));
      const codes = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPromoCodes(codes);
    } catch (error) {
      console.error('Error loading promo codes:', error);
      showToast('Error loading promo codes', 'error');
    }
    setLoading(false);
  }, [showToast]);

  // Create/Update promo code
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const promoData = {
        code: formData.code.toUpperCase().trim(),
        description: formData.description.trim(),
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),
        maxDiscount: formData.maxDiscount ? parseFloat(formData.maxDiscount) : null,
        minFare: formData.minFare ? parseFloat(formData.minFare) : null,
        minFinalFare: formData.minFinalFare ? parseFloat(formData.minFinalFare) : null,
        isActive: true,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        timesUsed: editingCode ? editingCode.timesUsed || 0 : 0,
        usedByUsers: editingCode ? editingCode.usedByUsers || [] : [],
        expiryDate: formData.expiryDate ? Timestamp.fromDate(new Date(formData.expiryDate)) : null,
        createdAt: editingCode ? editingCode.createdAt : Timestamp.now(),
        lastUsedAt: editingCode ? editingCode.lastUsedAt || null : null
      };

      if (editingCode) {
        await updateDoc(doc(db, 'promoCodes', editingCode.id), promoData);
        showToast('✅ Promo code updated successfully!', 'success');
      } else {
        await addDoc(collection(db, 'promoCodes'), promoData);
        showToast('✅ Promo code created successfully!', 'success');
      }

      resetForm();
      loadPromoCodes();
    } catch (error) {
      console.error('Error saving promo code:', error);
      showToast('❌ Error saving promo code', 'error');
    }
    setLoading(false);
  };

  // Delete promo code
  const deletePromoCode = async (id) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'promoCodes', id));
      showToast('✅ Promo code deleted successfully!', 'success');
      loadPromoCodes();
    } catch (error) {
      console.error('Error deleting promo code:', error);
      showToast('❌ Error deleting promo code', 'error');
    }
    setLoading(false);
  };

  // Start editing
  const startEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      description: code.description,
      discountType: code.discountType,
      discountValue: code.discountValue.toString(),
      maxDiscount: code.maxDiscount || '',
      minFare: code.minFare || '',
      minFinalFare: code.minFinalFare || '',
      usageLimit: code.usageLimit || '',
      expiryDate: code.expiryDate ? code.expiryDate.toDate().toISOString().split('T')[0] : ''
    });
    setShowCreateForm(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discountType: 'percentage',
      discountValue: '',
      maxDiscount: '',
      minFare: '',
      minFinalFare: '',
      usageLimit: '',
      expiryDate: ''
    });
    setEditingCode(null);
    setShowCreateForm(false);
  };

  // Cancel edit
  const cancelEdit = () => {
    resetForm();
  };

  // Quick setup with default promo codes
  const createDefaultPromoCodes = async () => {
    const defaultCodes = [
      {
        code: "WELCOME10",
        description: "10% off your first ride",
        discountType: "percentage",
        discountValue: 10,
        maxDiscount: 5.00,
        minFare: 10.00,
        minFinalFare: 5.00,
        isActive: true,
        usageLimit: 100,
        timesUsed: 0,
        usedByUsers: [],
        expiryDate: Timestamp.fromDate(new Date('2026-12-31')),
        createdAt: Timestamp.now(),
        lastUsedAt: null
      },
      {
        code: "SAVE5",
        description: "£5 off any ride",
        discountType: "fixed",
        discountValue: 5.00,
        maxDiscount: null,
        minFare: 15.00,
        minFinalFare: 5.00,
        isActive: true,
        usageLimit: 50,
        timesUsed: 0,
        usedByUsers: [],
        expiryDate: Timestamp.fromDate(new Date('2026-12-31')),
        createdAt: Timestamp.now(),
        lastUsedAt: null
      },
      {
        code: "NEWUSER",
        description: "20% off for new users",
        discountType: "percentage",
        discountValue: 20,
        maxDiscount: 10.00,
        minFare: 8.00,
        minFinalFare: 3.00,
        isActive: true,
        usageLimit: 200,
        timesUsed: 0,
        usedByUsers: [],
        expiryDate: Timestamp.fromDate(new Date('2025-12-31')),
        createdAt: Timestamp.now(),
        lastUsedAt: null
      }
    ];

    setLoading(true);
    try {
      for (const promoCode of defaultCodes) {
        await addDoc(collection(db, 'promoCodes'), promoCode);
      }
      showToast('✅ Default promo codes created successfully!', 'success');
      loadPromoCodes();
    } catch (error) {
      console.error('Error creating default promo codes:', error);
      showToast('❌ Error creating default promo codes', 'error');
    }
    setLoading(false);
  };

  // Manual booking functions
  const resetBookingForm = () => {
    setBookingData({
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      pickup: '',
      dropoff: '',
      numberOfRiders: 1,
      wheelchairRequired: false,
      fare: '',
      scheduledTime: '',
      notes: '',
      assignedDriverId: ''
    });
    setPickupCoords(null);
    setDropoffCoords(null);
    setDistanceInfo(null);
  };

  const handleManualBooking = async (e) => {
    e.preventDefault();
    console.log('🚀 handleManualBooking called!');
    console.log('📋 Current booking data:', bookingData);
    setBookingLoading(true);

    try {
      // Validate required fields
      if (!bookingData.customerName || !bookingData.customerName.trim()) {
        showToast('❌ Customer name is required', 'error');
        setBookingLoading(false);
        return;
      }

      if (!bookingData.customerPhone || !bookingData.customerPhone.trim()) {
        showToast('❌ Customer phone is required', 'error');
        setBookingLoading(false);
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (bookingData.customerEmail.trim() && !emailRegex.test(bookingData.customerEmail)) {
        showToast('❌ Please enter a valid email address', 'error');
        setBookingLoading(false);
        return;
      }

      if (!bookingData.pickup || !bookingData.pickup.trim()) {
        showToast('❌ Pickup address is required', 'error');
        setBookingLoading(false);
        return;
      }

      if (!bookingData.dropoff || !bookingData.dropoff.trim()) {
        showToast('❌ Drop-off address is required', 'error');
        setBookingLoading(false);
        return;
      }

      if (!bookingData.fare || isNaN(parseFloat(bookingData.fare))) {
        showToast('❌ Valid fare amount is required', 'error');
        setBookingLoading(false);
        return;
      }

      // Validate that we have distance information (if addresses are provided, distance should be calculated)
      if (bookingData.pickup && bookingData.dropoff && !distanceInfo) {
        showToast('❌ Please wait for route calculation to complete or enter fare manually', 'error');
        setBookingLoading(false);
        return;
      }

      const eligibleDrivers = availableDrivers.filter(driverMatchesBookingRequirements);

      if (availableDrivers.length > 0 && eligibleDrivers.length === 0) {
        showToast(
          bookingData.wheelchairRequired
            ? '❌ No wheelchair-accessible vehicle matches this booking.'
            : `❌ No vehicle with space for ${requiredVehicleCapacity} riders matches this booking.`,
          'error'
        );
        setBookingLoading(false);
        return;
      }

      // For scheduled bookings, automatically find and assign an available driver
      let assignedDriverId = bookingData.assignedDriverId || null;
      let assignedDriverName = null;
      let assignedDriverVehicle = null;
      
      if (bookingData.scheduledTime && !assignedDriverId) {
        console.log('🔍 Auto-assigning driver for scheduled booking...');
        
        try {
          const scheduledDrivers = await getScheduledDrivers(bookingData.scheduledTime);
          console.log('📋 Available scheduled drivers:', scheduledDrivers);
          
          if (scheduledDrivers.length > 0) {
            const selectedDriver = scheduledDrivers[0]; // Pick first available driver
            assignedDriverId = selectedDriver.id;
            assignedDriverName = selectedDriver.driverName || selectedDriver.name;
            assignedDriverVehicle = selectedDriver.driverVehicle || selectedDriver.vehicle;
            console.log(`✅ Auto-assigned driver: ${assignedDriverName} (${assignedDriverId})`);
            console.log(`🚗 Driver vehicle: ${assignedDriverVehicle}`);
          } else {
            showToast('❌ No drivers are available for the selected date and time', 'error');
            setBookingLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error finding available driver:', error);
          showToast('❌ Error finding available driver for scheduled time', 'error');
          setBookingLoading(false);
          return;
        }
      } else if (assignedDriverId) {
        // If driver was manually selected from dropdown, get their details
        console.log('🔍 Getting details for manually selected driver...');
        const selectedDriver = availableDrivers.find(d => d.id === assignedDriverId);
        if (selectedDriver) {
          if (!driverMatchesBookingRequirements(selectedDriver)) {
            showToast('❌ Selected driver does not meet the rider or wheelchair requirements', 'error');
            setBookingLoading(false);
            return;
          }
          assignedDriverName = selectedDriver.driverName || selectedDriver.name;
          assignedDriverVehicle = selectedDriver.driverVehicle || selectedDriver.vehicle;
          console.log(`✅ Manual driver selection: ${assignedDriverName} (${assignedDriverId})`);
          console.log(`🚗 Driver vehicle: ${assignedDriverVehicle}`);
        }
      }

      console.log('✅ All validation passed, creating booking...');
      console.log('📊 Distance info:', distanceInfo);
      console.log('👨‍✈️ Assigned driver:', assignedDriverId, assignedDriverName);
      console.log('🚗 Driver vehicle:', assignedDriverVehicle);

      const bookingId = `UGO-MANUAL-${Date.now()}`;
      
      const rideDoc = {
        // Customer details
        passengerName: bookingData.customerName,
        passengerPhone: bookingData.customerPhone,
        passengerEmail: bookingData.customerEmail.trim(),
        passengerId: `manual-${Date.now()}`, // Unique ID for manual bookings
        
        // Trip details
        pickupAddress: bookingData.pickup,
        dropoffAddress: bookingData.dropoff,
        pickup: bookingData.pickup,
        destination: bookingData.dropoff,
        
        // Location coordinates (if available from geocoding)
        pickupLatLng: pickupCoords ? new GeoPoint(pickupCoords.lat, pickupCoords.lng) : null,
        dropoffLatLng: dropoffCoords ? new GeoPoint(dropoffCoords.lat, dropoffCoords.lng) : null,
        
        // Distance and time information  
        distanceMiles: distanceInfo ? parseFloat(distanceInfo.distance) : 0,
        estimatedTripTime: distanceInfo ? distanceInfo.duration : 30, // Default 30 mins if not calculated
        numberOfRiders: requiredVehicleCapacity,
        requiredVehicleCapacity,
        wheelchairRequired: !!bookingData.wheelchairRequired,
        
        // Booking details
        calculatedFare: parseFloat(bookingData.fare),
        estimatedFare: parseFloat(bookingData.fare),
        originalFare: parseFloat(bookingData.fare),
        bookingId: bookingId,
        
        // Manual booking specific
        isManualBooking: true,
        manualBookingNotes: bookingData.notes || '',
        bookedBy: user.email,
        bookedAt: serverTimestamp(),
        
        // Status and timing - based on driver assignment
        status: assignedDriverId ? 
          (bookingData.scheduledTime ? 'awaiting_driver_response' : 'pending') : 
          (bookingData.scheduledTime ? 'scheduled' : 'pending'),
        bookingMode: bookingData.scheduledTime ? 'scheduled' : 'asap',
        isScheduled: !!bookingData.scheduledTime,
        scheduledTime: bookingData.scheduledTime ? 
          Timestamp.fromDate(new Date(bookingData.scheduledTime)) : null,
        
        // System fields
        timestamp: serverTimestamp(),
        passengerType: 'manual',
        assignedDriverId: assignedDriverId,
        currentOfferTo: assignedDriverId,
        driverName: assignedDriverName || (assignedDriverId ? 'Assigned Driver' : null),
        driverVehicle: assignedDriverVehicle || null
      };

      const rideRef = await addDoc(collection(db, 'riders'), rideDoc);
      
      const successMessage = assignedDriverName ? 
        `✅ Manual booking created successfully! ID: ${bookingId} | Driver: ${assignedDriverName}${assignedDriverVehicle ? ` (${assignedDriverVehicle})` : ''}` :
        `✅ Manual booking created successfully! ID: ${bookingId}`;
      
      showToast(successMessage, 'success');
      console.log('✅ Manual booking created with ID:', rideRef.id);
      if (assignedDriverName) {
        console.log('👨‍✈️ Assigned to driver:', assignedDriverName);
        if (assignedDriverVehicle) {
          console.log('🚗 Vehicle:', assignedDriverVehicle);
        }
      }
      
      // Reset form
      resetBookingForm();
      
    } catch (error) {
      console.error('Error creating manual booking:', error);
      showToast('❌ Error creating manual booking', 'error');
    }
    
    setBookingLoading(false);
  };

  // Login function
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      await signInWithEmailAndPassword(auth, loginData.email, loginData.password);
      showToast('✅ Logged in successfully!', 'success');
      setLoginData({ email: '', password: '' });
    } catch (error) {
      console.error('Login error:', error);
      showToast('❌ Invalid credentials', 'error');
    }
    setLoginLoading(false);
  };

  // Logout function
  const handleLogout = async () => {
    try {
      await signOut(auth);
      showToast('Logged out successfully', 'info');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const loadAvailableDrivers = useCallback(async () => {
    try {
      const driversSnap = await getDocs(collection(db, 'drivers'));
      const driversData = driversSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailableDrivers(driversData);
    } catch (error) {
      console.error('Error loading drivers:', error);
    }
  }, []);

  const loadDriverBookings = useCallback(async () => {
    if (!selectedDriverId) {
      setDriverBookings([]);
      return;
    }

    setDriverBookingsLoading(true);
    try {
      const ridesSnap = await getDocs(
        query(collection(db, 'riders'), where('assignedDriverId', '==', selectedDriverId))
      );

      const rides = ridesSnap.docs.map((rideDoc) => ({
        id: rideDoc.id,
        ...rideDoc.data(),
      }));

      rides.sort((a, b) => {
        const aTime = a.scheduledTime?.toMillis?.() || a.timestamp?.toMillis?.() || 0;
        const bTime = b.scheduledTime?.toMillis?.() || b.timestamp?.toMillis?.() || 0;
        return bTime - aTime;
      });

      setDriverBookings(rides);
    } catch (error) {
      console.error('Error loading driver bookings:', error);
      showToast('❌ Could not load driver bookings', 'error');
      setDriverBookings([]);
    }
    setDriverBookingsLoading(false);
  }, [selectedDriverId, showToast]);

  const transferRideToDriver = async (ride) => {
    const targetDriverId = transferSelection[ride.id];

    if (!targetDriverId) {
      showToast('❌ Select a driver to transfer this booking', 'error');
      return;
    }

    if (targetDriverId === ride.assignedDriverId) {
      showToast('❌ Booking is already assigned to that driver', 'error');
      return;
    }

    const normalizedStatus = (ride.status || '').toLowerCase();
    if (!TRANSFERABLE_RIDE_STATUSES.includes(normalizedStatus)) {
      showToast('❌ Only upcoming bookings can be transferred', 'error');
      return;
    }

    const targetDriver = availableDrivers.find((driver) => driver.id === targetDriverId);
    if (!targetDriver) {
      showToast('❌ Selected driver not found', 'error');
      return;
    }

    setTransferLoadingRideId(ride.id);
    try {
      await updateDoc(doc(db, 'riders', ride.id), {
        assignedDriverId: targetDriver.id,
        currentOfferTo: targetDriver.id,
        driverName: targetDriver.driverName || targetDriver.name || null,
        driverVehicle: targetDriver.driverVehicle || targetDriver.vehicle || null,
        status: 'awaiting_driver_response',
        transferredAt: serverTimestamp(),
        transferredBy: user?.email || 'admin',
      });

      setTransferSelection((prev) => ({
        ...prev,
        [ride.id]: '',
      }));

      showToast('✅ Booking transferred to new driver', 'success');
      loadDriverBookings();
    } catch (error) {
      console.error('Error transferring ride:', error);
      showToast('❌ Failed to transfer booking', 'error');
    }
    setTransferLoadingRideId('');
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((nextUser) => {
      setUser(nextUser);
      if (nextUser?.email === ADMIN_EMAIL) {
        loadPromoCodes();
        loadAvailableDrivers();
      }
    });
    return () => unsubscribe();
  }, [loadAvailableDrivers, loadPromoCodes]);

  // Also load drivers when component mounts if user is already logged in
  useEffect(() => {
    if (isAdminUser) {
      loadAvailableDrivers();
    }
  }, [isAdminUser, loadAvailableDrivers]);

  useEffect(() => {
    if (!selectedDriverId && availableDrivers.length > 0) {
      setSelectedDriverId(availableDrivers[0].id);
    }
  }, [availableDrivers, selectedDriverId]);

  useEffect(() => {
    if (activeTab === 'driverBookings') {
      loadDriverBookings();
    }
  }, [activeTab, loadDriverBookings]);

  // Helper functions for scheduled driver availability (same as TripDetailsScreen)
  const getDayName = useCallback((date) => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    return days[date.getDay() === 0 ? 6 : date.getDay() - 1]; // Convert Sunday=0 to Monday=0
  }, []);

  const timeStringToMinutes = useCallback((timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }, []);

  const isTimeInRange = useCallback((timeToCheck, startTime, finishTime) => {
    const checkMinutes = timeStringToMinutes(timeToCheck);
    const startMinutes = timeStringToMinutes(startTime);
    const finishMinutes = timeStringToMinutes(finishTime);
    return checkMinutes >= startMinutes && checkMinutes <= finishMinutes;
  }, [timeStringToMinutes]);

  // Get available drivers for scheduled bookings (same logic as TripDetailsScreen)
  const getScheduledDrivers = useCallback(async (scheduledDateTime) => {
    if (!scheduledDateTime) return availableDrivers;

    try {
      const bookingDate = new Date(scheduledDateTime);
      const lowerBound = new Date(bookingDate.getTime() - 30 * 60000);
      const upperBound = new Date(bookingDate.getTime() + 30 * 60000);

      const dayName = getDayName(bookingDate);
      const timeStr = bookingDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
      const dateStr = bookingDate.toISOString().split('T')[0];
      const eligibleDrivers = availableDrivers.filter(driverMatchesBookingRequirements);

      console.log(`🔍 Checking drivers for scheduled time: ${timeStr} on ${dayName} (${dateStr})`);
      console.log(`📋 Total drivers to check: ${availableDrivers.length}`);
      console.log(`✅ Drivers matching requirements: ${eligibleDrivers.length}`);

      const scheduledAvailableDrivers = [];

      // For scheduled rides, check ALL drivers (not just online ones)
      for (const driver of eligibleDrivers) {
        const availability = driver.availability || [];
        const unavailableDates = driver.unavailableDates || [];
        const driverName = driver.driverName || driver.name || 'Unknown';

        console.log(`👨‍✈️ Checking driver: ${driverName}`);
        console.log(`   - Availability: ${JSON.stringify(availability)}`);
        console.log(`   - Unavailable dates: ${JSON.stringify(unavailableDates)}`);

        if (unavailableDates.includes(dateStr)) {
          console.log(`   ❌ Driver unavailable on ${dateStr}`);
          continue;
        }

        const dayMatch = availability.find(day => {
          const startTime = day.start?.toString() || '';
          const finishTime = day.finish?.toString() || '';
          const dayNameMatch = day.day?.toString().toLowerCase() || '';
          
          console.log(`   🕐 Checking availability: ${dayNameMatch} ${startTime}-${finishTime} vs ${dayName} ${timeStr}`);
          
          return dayNameMatch === dayName && isTimeInRange(timeStr, startTime, finishTime);
        });

        if (dayMatch) {
          console.log(`   ✅ Day/time match found for ${driverName}`);
          // Check for conflicting rides
          const conflictingSnap = await getDocs(
            query(
              collection(db, 'riders'),
              where('assignedDriverId', '==', driver.id),
              where('scheduledTime', '>=', Timestamp.fromDate(lowerBound)),
              where('scheduledTime', '<=', Timestamp.fromDate(upperBound)),
              where('status', 'in', ['pending', 'accepted', 'awaiting_driver_response'])
            )
          );

          console.log(`   🔍 Found ${conflictingSnap.docs.length} conflicting rides for ${driverName}`);
          
          if (conflictingSnap.empty) {
            console.log(`   ✅ No conflicts! Adding ${driverName} to available drivers`);
            scheduledAvailableDrivers.push(driver);
          } else {
            console.log(`   ❌ Conflicts found for ${driverName}`);
          }
        } else {
          console.log(`   ❌ No day/time match for ${driverName}`);
        }
      }

      console.log(`📊 Final available drivers: ${scheduledAvailableDrivers.length}`);
      return scheduledAvailableDrivers;
    } catch (error) {
      console.error('Error getting scheduled drivers:', error);
      return [];
    }
  }, [availableDrivers, driverMatchesBookingRequirements, getDayName, isTimeInRange]);

  // Get filtered drivers based on booking type
  const getFilteredDrivers = useCallback(async () => {
    if (bookingData.scheduledTime) {
      // For scheduled bookings, get drivers available at that time
      return await getScheduledDrivers(bookingData.scheduledTime);
    } else {
      return availableDrivers.filter(driver => driver.isOnline && driverMatchesBookingRequirements(driver));
    }
  }, [bookingData.scheduledTime, availableDrivers, getScheduledDrivers, driverMatchesBookingRequirements]);

  // Real-time fare calculation when addresses change
  useEffect(() => {
    if (!pickupCoords || !dropoffCoords) {
      setBookingData(prev => ({ ...prev, fare: '' }));
      setDistanceInfo(null);
      return;
    }

    // Calculate route and fare like in RiderMap
    if (window.google && window.google.maps) {
      const directionsService = new window.google.maps.DirectionsService();

      directionsService.route(
        {
          origin: pickupCoords,
          destination: dropoffCoords,
          travelMode: 'DRIVING',
        },
        (result, status) => {
          if (status === 'OK' && result.routes.length) {
            const route = result.routes[0];
            const leg = route.legs[0];

            const yards = leg.distance?.value * 1.09361;

            // Get applicable tariff (same as RiderMap)
            const targetTime = bookingData.scheduledTime ? new Date(bookingData.scheduledTime) : new Date();
            const tariff = getApplicableTariff(targetTime);
            
            // Calculate fare
            const calculatedFare = calculateFare(yards, tariff, { numberOfRiders: bookingData.numberOfRiders });
            setBookingData(prev => ({ ...prev, fare: calculatedFare.toFixed(2) }));

            setDistanceInfo({
              distance: (leg.distance?.value / 1609.34).toFixed(2),
              duration: Math.round(leg.duration?.value / 60),
              tariff: tariff.name
            });
          }
        }
      );
    }
  }, [pickupCoords, dropoffCoords, bookingData.scheduledTime, bookingData.numberOfRiders]);

  // Update filtered drivers when scheduled time or available drivers change
  useEffect(() => {
    const updateFilteredDrivers = async () => {
      try {
        const drivers = await getFilteredDrivers();
        setFilteredDrivers(drivers);
      } catch (error) {
        console.error('❌ Error updating filtered drivers:', error);
        setFilteredDrivers([]);
      }
    };
    
    if (availableDrivers.length > 0) {
      updateFilteredDrivers();
    } else {
      setFilteredDrivers([]);
    }
  }, [availableDrivers, getFilteredDrivers]);

  // Login form
  if (!user) {
    return (
      <div className="admin-panel">
        <div className="admin-login">
          <h2>🔐 Admin Login</h2>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                placeholder="admin@u-gotaxi.co.uk"
                required
              />
            </div>
            
            <div className="form-group">
              <label>Password:</label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                placeholder="Enter your password"
                required
              />
            </div>
            
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loginLoading}>
                {loginLoading ? '🔄 Logging in...' : '🔑 Login'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => navigate('/')}>
                🏠 Back to Home
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  const upcomingDriverBookings = driverBookings.filter((ride) => {
    const status = (ride.status || '').toLowerCase();
    return !TERMINAL_RIDE_STATUSES.includes(status);
  });

  const pastDriverBookings = driverBookings.filter((ride) => {
    const status = (ride.status || '').toLowerCase();
    return TERMINAL_RIDE_STATUSES.includes(status);
  });

  if (!isAdminUser) {
    return (
      <div className="admin-panel">
        <div className="admin-access-denied">
          <h2>❌ Access Denied</h2>
          <p>You don't have permission to access the admin panel.</p>
          <p>Current user: {user.email}</p>
          <div className="form-actions">
            <button className="btn-secondary" onClick={handleLogout}>
              🚪 Logout
            </button>
            <button className="btn-secondary" onClick={() => navigate('/')}>
              🏠 Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div>
          <h1>🛠️ Admin Dashboard</h1>
          <p style={{margin: '5px 0 0 0', color: '#6c757d', fontSize: '14px'}}>
            Logged in as: {user.email}
          </p>
          
          {/* Tab Navigation */}
          <div style={{ marginTop: '15px' }}>
            <button 
              onClick={() => setActiveTab('promos')}
              style={{
                padding: '8px 16px',
                marginRight: '10px',
                backgroundColor: activeTab === 'promos' ? '#007bff' : '#f8f9fa',
                color: activeTab === 'promos' ? 'white' : '#495057',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🎟️ Promo Codes
            </button>
            <button 
              onClick={() => setActiveTab('booking')}
              style={{
                padding: '8px 16px',
                marginRight: '10px',
                backgroundColor: activeTab === 'booking' ? '#007bff' : '#f8f9fa',
                color: activeTab === 'booking' ? 'white' : '#495057',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              📞 Manual Booking
            </button>
            <button
              onClick={() => setActiveTab('driverBookings')}
              style={{
                padding: '8px 16px',
                backgroundColor: activeTab === 'driverBookings' ? '#007bff' : '#f8f9fa',
                color: activeTab === 'driverBookings' ? 'white' : '#495057',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🚕 Driver Bookings
            </button>
          </div>
        </div>
        <div className="admin-actions">
          {activeTab === 'promos' && (
            <>
              <button 
                className="btn-primary"
                onClick={() => setShowCreateForm(!showCreateForm)}
              >
                {showCreateForm ? 'Cancel' : '+ New Promo Code'}
              </button>
              {promoCodes.length === 0 && (
                <button 
                  className="btn-success"
                  onClick={createDefaultPromoCodes}
                  disabled={loading}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white'
                  }}
                >
                  🚀 Quick Setup
                </button>
              )}
              <button 
                className="btn-secondary"
                onClick={loadPromoCodes}
                disabled={loading}
              >
                🔄 Refresh
              </button>
            </>
          )}
          {activeTab === 'driverBookings' && (
            <button
              className="btn-secondary"
              onClick={loadDriverBookings}
              disabled={driverBookingsLoading || !selectedDriverId}
            >
              🔄 Refresh Bookings
            </button>
          )}
          <button 
            className="btn-secondary"
            onClick={handleLogout}
            style={{
              backgroundColor: '#dc3545',
              color: 'white'
            }}
          >
            🚪 Logout
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'promos' && (
        <div className="promo-management">

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="promo-form">
          <h3>{editingCode ? 'Edit Promo Code' : 'Create New Promo Code'}</h3>
          
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  placeholder="WELCOME10"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Description *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="10% off your first ride"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Discount Type *</label>
                <select
                  value={formData.discountType}
                  onChange={(e) => setFormData({...formData, discountType: e.target.value})}
                  required
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (£)</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Discount Value *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({...formData, discountValue: e.target.value})}
                  placeholder={formData.discountType === 'percentage' ? '10' : '5.00'}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Max Discount (£)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.maxDiscount}
                  onChange={(e) => setFormData({...formData, maxDiscount: e.target.value})}
                  placeholder="5.00"
                />
              </div>
              
              <div className="form-group">
                <label>Min Fare (£)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.minFare}
                  onChange={(e) => setFormData({...formData, minFare: e.target.value})}
                  placeholder="10.00"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Min Final Fare (£)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.minFinalFare}
                  onChange={(e) => setFormData({...formData, minFinalFare: e.target.value})}
                  placeholder="3.00"
                />
              </div>
              
              <div className="form-group">
                <label>Usage Limit (blank = unlimited)</label>
                <input
                  type="number"
                  value={formData.usageLimit}
                  onChange={(e) => setFormData({...formData, usageLimit: e.target.value})}
                  placeholder="100"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Expiry Date (optional)</label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? '🔄 Saving...' : (editingCode ? '✅ Update' : '✅ Create')}
              </button>
              <button type="button" className="btn-secondary" onClick={cancelEdit}>
                ❌ Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Promo Codes List */}
      <div className="promo-list">
        <h3>📋 Active Promo Codes ({promoCodes.length})</h3>
        
        {loading && <div className="loading">🔄 Loading...</div>}
        
        {promoCodes.map(code => (
          <div key={code.id} className="promo-card">
            <div className="promo-header">
              <h4>{code.code}</h4>
              <span className={`status ${code.isActive ? 'active' : 'inactive'}`}>
                {code.isActive ? '✅ Active' : '❌ Inactive'}
              </span>
            </div>
            
            <div className="promo-details">
              <div className="detail-item">
                <strong>Description:</strong> {code.description}
              </div>
              
              <div className="detail-item">
                <strong>Discount:</strong> 
                {code.discountType === 'percentage' 
                  ? `${code.discountValue}%` 
                  : `£${code.discountValue}`}
                {code.maxDiscount && ` (max £${code.maxDiscount})`}
              </div>
              
              <div className="detail-item">
                <strong>Min Fare:</strong> £{code.minFare}
              </div>
              
              <div className="detail-item">
                <strong>Usage Tracking:</strong> 
                <div style={{ marginTop: '5px', fontSize: '13px' }}>
                  <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                    {code.timesUsed || 0}
                  </span>
                  {code.usageLimit ? ` / ${code.usageLimit}` : ' (unlimited)'} times used
                  {code.usedByUsers && code.usedByUsers.length > 0 && (
                    <div style={{ marginTop: '3px', color: '#6c757d' }}>
                      🎯 {code.usedByUsers.length} unique user{code.usedByUsers.length !== 1 ? 's' : ''}
                      {code.lastUsedAt && (
                        <span style={{ marginLeft: '10px' }}>
                          📅 Last used: {code.lastUsedAt.toDate().toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {code.expiryDate && (
                <div className="detail-item">
                  <strong>Expires:</strong> {code.expiryDate.toDate().toLocaleDateString()}
                </div>
              )}
            </div>
            
            <div className="promo-actions">
              <button 
                className="btn-edit"
                onClick={() => startEdit(code)}
                disabled={loading}
              >
                ✏️ Edit
              </button>
              <button 
                className="btn-delete"
                onClick={() => deletePromoCode(code.id)}
                disabled={loading}
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
      </div>
        </div>
      )}

      {/* Manual Booking Tab */}
      {activeTab === 'booking' && (
        <div className="manual-booking">
          <h3>📞 Manual Ride Booking</h3>
          <p style={{ color: '#6c757d', marginBottom: '20px' }}>
            Create ride bookings manually for phone orders or special arrangements.
          </p>

          {!isLoaded ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div>🗺️ Loading Google Maps...</div>
            </div>
          ) : (
            <form onSubmit={handleManualBooking} style={{ maxWidth: '800px' }}>
            <div className="form-row">
              <div className="form-group">
                <label>Customer Name *</label>
                <input
                  type="text"
                  value={bookingData.customerName}
                  onChange={(e) => setBookingData({...bookingData, customerName: e.target.value})}
                  placeholder="John Smith"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Customer Phone *</label>
                <input
                  type="tel"
                  value={bookingData.customerPhone}
                  onChange={(e) => setBookingData({...bookingData, customerPhone: e.target.value})}
                  placeholder="07123456789"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                  <label>Customer Email</label>
                <input
                  type="email"
                  value={bookingData.customerEmail}
                  onChange={(e) => setBookingData({...bookingData, customerEmail: e.target.value})}
                  placeholder="john@example.com"
                />
                <small style={{ color: '#6c757d', fontSize: '12px' }}>
                  📧 Optional. Leave blank if no email is available.
                </small>
              </div>
              
              <div className="form-group">
                <label>Fare (£) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={bookingData.fare}
                  onChange={(e) => setBookingData({...bookingData, fare: e.target.value})}
                  placeholder="15.50"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Number of Riders *</label>
                <select
                  value={bookingData.numberOfRiders}
                  onChange={(e) => setBookingData({ ...bookingData, numberOfRiders: parseInt(e.target.value, 10), assignedDriverId: '' })}
                  style={{
                    padding: '12px',
                    borderRadius: '7px',
                    border: '2px solid #00ab86',
                    width: '100%',
                    fontSize: '16px',
                    backgroundColor: 'white',
                  }}
                >
                  <option value={1}>1 Person</option>
                  <option value={2}>2 People</option>
                  <option value={3}>3 People</option>
                  <option value={4}>4 People</option>
                  <option value={5}>5 People</option>
                </select>
                {bookingData.numberOfRiders > LARGE_PARTY_THRESHOLD && (
                  <small style={{ color: '#6c757d', fontSize: '12px' }}>
                    £{LARGE_PARTY_SURCHARGE.toFixed(2)} extra applies for over 4 riders.
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Accessibility</label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  minHeight: '48px',
                  padding: '12px',
                  borderRadius: '7px',
                  border: '2px solid #00ab86',
                  backgroundColor: '#fff'
                }}>
                  <input
                    id="admin-wheelchair-required"
                    type="checkbox"
                    checked={bookingData.wheelchairRequired}
                    onChange={(e) => setBookingData({ ...bookingData, wheelchairRequired: e.target.checked, assignedDriverId: '' })}
                  />
                  <label htmlFor="admin-wheelchair-required" style={{ margin: 0 }}>
                    Wheelchair accessible vehicle required
                  </label>
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Pickup Address *</label>
                <AddressSearch
                  placeholder="123 Main Street, City"
                  onSelect={(coords, address) => {
                    setPickupCoords(coords);
                    setBookingData({...bookingData, pickup: address});
                  }}
                />
              </div>
              
              <div className="form-group">
                <label>Drop-off Address *</label>
                <AddressSearch
                  placeholder="456 High Street, City"
                  onSelect={(coords, address) => {
                    setDropoffCoords(coords);
                    setBookingData({...bookingData, dropoff: address});
                  }}
                />
              </div>
            </div>

            {/* Real-time Fare Display */}
            {distanceInfo && (
              <div className="form-row">
                <div className="form-group" style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <strong>📍 Trip Details</strong>
                    <span style={{ color: '#28a745', fontWeight: 'bold', fontSize: '16px' }}>
                      £{bookingData.fare}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', fontSize: '14px' }}>
                    <div>🛣️ {distanceInfo.distance} miles</div>
                    <div>⏱️ {distanceInfo.duration} mins</div>
                    <div>🏷️ {distanceInfo.tariff}</div>
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '14px', color: '#495057' }}>
                    <div>👥 {requiredVehicleCapacity} {requiredVehicleCapacity === 1 ? 'rider' : 'riders'}</div>
                    {bookingData.wheelchairRequired && <div>♿ Wheelchair accessible vehicle required</div>}
                  </div>
                </div>
              </div>
            )}

            {/* Driver Assignment */}
            <div className="form-row">
              <div className="form-group">
                <label>Assign Driver (Optional)</label>
                <select
                  value={bookingData.assignedDriverId}
                  onChange={(e) => setBookingData({...bookingData, assignedDriverId: e.target.value})}
                  style={{
                    padding: '12px',
                    borderRadius: '7px',
                    border: '2px solid #00ab86',
                    width: '100%',
                    fontSize: '16px',
                    backgroundColor: 'white',
                  }}
                >
                  <option value="">
                    {bookingData.scheduledTime 
                      ? 'Auto-assign from matching scheduled drivers' 
                      : 'Auto-assign from matching online drivers'}
                  </option>
                  {filteredDrivers.map(driver => (
                    <option key={driver.id} value={driver.id}>
                      🧑‍💼 {driver.driverName || driver.name} ({driver.phone})
                      {bookingData.scheduledTime 
                        ? ` - 📅 Available | ${getDriverVehicleCapacity(driver)} seats${driver.wheelchairAccessible ? ' | Wheelchair' : ''}`
                        : ` - 🟢 Online | ${getDriverVehicleCapacity(driver)} seats${driver.wheelchairAccessible ? ' | Wheelchair' : ''}`}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#6c757d', fontSize: '12px' }}>
                  {bookingData.scheduledTime 
                    ? 'Showing drivers available for the selected date/time who also match the rider capacity and wheelchair requirement.'
                    : 'Showing online drivers who match the rider capacity and wheelchair requirement.'}
                </small>
                {bookingData.scheduledTime && filteredDrivers.length === 0 && (
                  <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
                    <small style={{ color: '#856404' }}>
                      {bookingData.wheelchairRequired
                        ? '⚠️ No wheelchair-accessible drivers are available for the selected time.'
                        : `⚠️ No drivers with space for ${requiredVehicleCapacity} riders are available for the selected time.`}
                    </small>
                  </div>
                )}
                {!bookingData.scheduledTime && filteredDrivers.length === 0 && availableDrivers.length > 0 && (
                  <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
                    <small style={{ color: '#856404' }}>
                      {bookingData.wheelchairRequired
                        ? '⚠️ No wheelchair-accessible drivers are currently online.'
                        : `⚠️ No drivers with space for ${requiredVehicleCapacity} riders are currently online.`}
                    </small>
                  </div>
                )}
              </div>
            </div>

            {/* Manual Fare Override */}
            <div className="form-row">
              <div className="form-group">
                <label>Manual Fare Override (£)</label>
                <input
                  type="number"
                  step="0.01"
                  value={bookingData.fare}
                  onChange={(e) => setBookingData({...bookingData, fare: e.target.value})}
                  placeholder={distanceInfo ? "Auto-calculated - override if needed" : "Will auto-calculate when addresses entered"}
                  required
                  style={{
                    backgroundColor: distanceInfo ? '#d4edda' : 'white',
                    borderColor: distanceInfo ? '#28a745' : '#ced4da',
                    padding: '12px',
                    borderRadius: '7px',
                    border: '2px solid',
                    width: '100%',
                    fontSize: '16px',
                  }}
                />
                {distanceInfo && (
                  <small style={{ color: '#28a745' }}>
                    ✅ Auto-calculated based on {distanceInfo.tariff} tariff
                  </small>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Scheduled Time (leave blank for ASAP)</label>
                <input
                  type="datetime-local"
                  value={bookingData.scheduledTime}
                  onChange={(e) => setBookingData({...bookingData, scheduledTime: e.target.value})}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
              
              <div className="form-group">
                <label>Notes (optional)</label>
                <input
                  type="text"
                  value={bookingData.notes}
                  onChange={(e) => setBookingData({...bookingData, notes: e.target.value})}
                  placeholder="Special instructions..."
                />
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn-primary" 
                disabled={bookingLoading}
                onClick={() => console.log('🔥 Create Booking button clicked!')}
              >
                {bookingLoading ? '🔄 Creating Booking...' : '✅ Create Booking'}
              </button>
              <button type="button" className="btn-secondary" onClick={resetBookingForm}>
                🔄 Reset Form
              </button>
            </div>
          </form>
          )}
        </div>
      )}

      {activeTab === 'driverBookings' && (
        <div className="driver-bookings-tab">
          <h3>🚕 Driver Bookings</h3>
          <p style={{ color: '#6c757d', marginBottom: '16px' }}>
            View bookings per driver and transfer upcoming jobs without cancelling and rebooking.
          </p>

          <div className="driver-bookings-controls">
            <div className="form-group" style={{ maxWidth: '360px', marginBottom: 0 }}>
              <label>Select Driver</label>
              <select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
              >
                <option value="">Choose a driver...</option>
                {availableDrivers.map((driver) => (
                  <option key={driver.id} value={driver.id}>
                    {driver.driverName || driver.name || 'Unknown Driver'}
                    {driver.driverVehicle ? ` - ${driver.driverVehicle}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {driverBookingsLoading && <div className="loading">🔄 Loading driver bookings...</div>}

          {!driverBookingsLoading && selectedDriverId && (
            <>
              <div className="driver-booking-summary">
                <span>Upcoming: {upcomingDriverBookings.length}</span>
                <span>Past: {pastDriverBookings.length}</span>
              </div>

              <div className="driver-booking-section">
                <h4>⏭️ Upcoming Bookings</h4>
                {upcomingDriverBookings.length === 0 && (
                  <div className="driver-bookings-empty">No upcoming bookings for this driver.</div>
                )}

                {upcomingDriverBookings.map((ride) => (
                  <div key={ride.id} className="driver-booking-card">
                    <div className="driver-booking-card-header">
                      <strong>{ride.bookingId || ride.id}</strong>
                      <span className="driver-booking-status">{getRideDisplayStatus(ride.status)}</span>
                    </div>

                    <div className="driver-booking-grid">
                      <div><strong>Passenger:</strong> {ride.passengerName || 'N/A'}</div>
                      <div><strong>Phone:</strong> {ride.passengerPhone || 'N/A'}</div>
                      <div><strong>Pickup:</strong> {ride.pickupAddress || ride.pickup || 'N/A'}</div>
                      <div><strong>Drop-off:</strong> {ride.dropoffAddress || ride.destination || 'N/A'}</div>
                      <div><strong>Fare:</strong> £{Number(ride.calculatedFare || ride.estimatedFare || 0).toFixed(2)}</div>
                      <div><strong>Scheduled:</strong> {formatFirebaseDateTime(ride.scheduledTime || ride.timestamp)}</div>
                    </div>

                    <div className="driver-transfer-row">
                      <select
                        value={transferSelection[ride.id] || ''}
                        onChange={(e) => setTransferSelection((prev) => ({ ...prev, [ride.id]: e.target.value }))}
                      >
                        <option value="">Transfer to another driver...</option>
                        {availableDrivers
                          .filter((driver) => driver.id !== ride.assignedDriverId)
                          .map((driver) => (
                            <option key={driver.id} value={driver.id}>
                              {driver.driverName || driver.name || 'Unknown Driver'}
                              {driver.driverVehicle ? ` - ${driver.driverVehicle}` : ''}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={transferLoadingRideId === ride.id || !transferSelection[ride.id]}
                        onClick={() => transferRideToDriver(ride)}
                      >
                        {transferLoadingRideId === ride.id ? '🔄 Transferring...' : '🔁 Transfer Job'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="driver-booking-section">
                <h4>🕘 Past Bookings</h4>
                {pastDriverBookings.length === 0 && (
                  <div className="driver-bookings-empty">No past bookings for this driver.</div>
                )}

                {pastDriverBookings.map((ride) => (
                  <div key={ride.id} className="driver-booking-card past">
                    <div className="driver-booking-card-header">
                      <strong>{ride.bookingId || ride.id}</strong>
                      <span className="driver-booking-status">{getRideDisplayStatus(ride.status)}</span>
                    </div>
                    <div className="driver-booking-grid">
                      <div><strong>Passenger:</strong> {ride.passengerName || 'N/A'}</div>
                      <div><strong>Phone:</strong> {ride.passengerPhone || 'N/A'}</div>
                      <div><strong>Pickup:</strong> {ride.pickupAddress || ride.pickup || 'N/A'}</div>
                      <div><strong>Drop-off:</strong> {ride.dropoffAddress || ride.destination || 'N/A'}</div>
                      <div><strong>Fare:</strong> £{Number(ride.calculatedFare || ride.estimatedFare || 0).toFixed(2)}</div>
                      <div><strong>Date:</strong> {formatFirebaseDateTime(ride.scheduledTime || ride.timestamp)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {!driverBookingsLoading && !selectedDriverId && (
            <div className="driver-bookings-empty">Select a driver to view bookings.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
